import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  DollarSign,
  Activity,
  Plus,
  ArrowUpRight,
  Clock,
  CheckCircle2,
  AlertTriangle,
  Loader2,
  Zap,
  TrendingUp,
  Shield,
  FileText,
  ChevronRight,
  Cpu, Download } from 'lucide-react';

import { PORTCOS, AGENTS } from '../mock/platformData';
import { usePortCo } from '../context/PortCoContext';
import { rcmApi, type RcmDashboardResponse, type RcmClaimsResponse, type RcmApproval } from '../api/rcmApi';
import type { AgentStatus } from '../types';
import { API_BASE, apiFetch } from '../api/base';

interface LiveRun {
  id: string;
  agentName: string;
  summary: string;
  status: string;
  startTime: string;
  endTime: string;
  claimsAnalyzed: number;
  denialsFound: number;
  appealsGenerated: number;
  avgConfidence: number;
}

export const ExecutiveDashboard: React.FC = () => {
  const { selectedPortco, selectedPortcoName, portcos: visiblePortcos } = usePortCo();

  const [packetBusy, setPacketBusy] = useState(false);
  const downloadPacket = async (claimId?: string, bundle = false) => {
    setPacketBusy(true);
    try {
      const html = await rcmApi.fetchAppealPacket(selectedPortco, claimId, bundle);
      if (html) {
        const blob = new Blob([html], { type: 'text/html' });
        const url = URL.createObjectURL(blob);
        window.open(url, '_blank');
        setTimeout(() => URL.revokeObjectURL(url), 60000);
      }
    } finally {
      setPacketBusy(false);
    }
  };
  const navigate = useNavigate();

  const [rcmData, setRcmData] = useState<RcmDashboardResponse | null>(null);
  const [rcmClaims, setRcmClaims] = useState<RcmClaimsResponse | null>(null);
  const [approvals, setApprovals] = useState<RcmApproval[]>([]);
  const [loading, setLoading] = useState(true);
  const [triggering, setTriggering] = useState(false);
  const [fleet, setFleet] = useState<{
    summary: { activeAgents: number; runningNow: number; pendingApprovals: number; actionsToday: number; tokensUsedTotal: number; tokensUsedToday: number; lastRunAt: string | null };
    agents: Array<{ id: string; name: string; portcoId: string; portcoName?: string; status: 'running' | 'active' | 'idle'; lastRunAt: string; tokensUsed: number; runsTotal: number; actionsToday: number; successRate: number | null; workstream: string }>;
  } | null>(null);

  // Silent background refresh: only the FIRST load for a PortCo (or a
  // PortCo switch) shows loading spinners. The 15s poll updates data in
  // place so the dashboard never flickers/blank-reloads.
  const loadedPortcoRef = useRef<string | null>(null);
  const inFlightRef = useRef(false);

  const fetchAllData = useCallback(async () => {
    if (inFlightRef.current) return; // don't stack overlapping polls
    inFlightRef.current = true;
    const silent = loadedPortcoRef.current === selectedPortco;
    if (!silent) setLoading(true);
    try {
      const [dashboard, claims, approvalsRes, fleetRes] = await Promise.all([
        rcmApi.getDashboard(selectedPortco).catch(() => null),
        rcmApi.getClaims(selectedPortco).catch(() => null),
        rcmApi.getApprovals(selectedPortco).catch(() => null),
        apiFetch(`${API_BASE}/fleet`).then(r => r.ok ? r.json() : null).catch(() => null),
      ]);
      // Keep previous data when an endpoint fails; refresh silently otherwise
      if (dashboard) setRcmData(dashboard);
      if (claims) setRcmClaims(claims);
      if (approvalsRes) setApprovals(approvalsRes.approvals ?? []);
      if (fleetRes) setFleet(fleetRes);
      loadedPortcoRef.current = selectedPortco;
    } finally {
      inFlightRef.current = false;
      setLoading(false);
    }
  }, [selectedPortco]);

  useEffect(() => {
    fetchAllData();
    const interval = setInterval(fetchAllData, 15000);
    return () => clearInterval(interval);
  }, [fetchAllData]);

  // Tenant-scoped business list: what the signed-in user is allowed to see
  // (registry-filtered for tenant admins, full registry for portfolio admins).
  // Mock detail is merged in only for ids the user can access — a Summit
  // viewer must never see Apex cards in "At a Glance".
  const safePortcos = (visiblePortcos.length ? visiblePortcos : []).map((v) => {
    const mock = PORTCOS.find((p) => p.id === v.id);
    return mock
      ? { ...mock, name: v.name || mock.name }
      : { id: v.id, name: v.name, code: v.code || '', region: v.region || '',
          monthlyRevenue: '$0M', patients: 0, staff: 0, complianceScore: 100, kpis: [] as never[] };
  });
  const filteredPortcos = safePortcos.filter((p) => selectedPortco === 'all' || p.id === selectedPortco);
  const fleetAgents = (fleet?.agents ?? []).filter((a) => selectedPortco === 'all' || a.portcoId === selectedPortco);
  const filteredAgents = fleetAgents;

  const totalRevenue = selectedPortco === 'all'
    ? `$${safePortcos.reduce((s, p) => s + parseFloat(p.monthlyRevenue.replace(/[^\d.]/g, '')) || 0, 0).toFixed(2)}M`
    : safePortcos.find((p) => p.id === selectedPortco)?.monthlyRevenue ?? '$0';
  const totalPatients = filteredPortcos.reduce((sum, p) => sum + (p.patients || 0), 0).toLocaleString();
  const totalStaff = filteredPortcos.reduce((sum, p) => sum + (p.staff || 0), 0).toLocaleString();

  const metrics = rcmData?.metrics;
  const totalClaims = metrics?.totalClaims ?? 0;
  const deniedClaims = metrics?.deniedClaims ?? 0;
  const denialAmount = metrics?.totalDenialAmount ?? 0;
  const agentRunsCount = metrics?.agentRunsCount ?? 0;
  const successRate = metrics?.successRate ?? 0;
  const pendingApprovalsCount = approvals.length;

  const recentRuns: LiveRun[] = rcmData?.recentRuns ?? [];
  const runningRuns = recentRuns.filter(r => r.status === 'running');
  const completedRuns = recentRuns.filter(r => r.status === 'completed');

  const recoveredAmount = denialAmount;
  const automationHoursSaved = agentRunsCount * 4.5;
  const totalSavings = recoveredAmount + (automationHoursSaved * 45);
  const monthlySavings = totalSavings > 0 ? `$${(totalSavings / 1000).toFixed(1)}K` : '$0';

  const workstreamCoverage = agentRunsCount > 0 ? 100 : 0;
  const complianceScore = Math.round(
    (successRate * 0.4) + (workstreamCoverage * 0.3) + (100 * 0.3)
  );

  const activeAgents = fleet?.summary
    ? [{ status: 'x' }, ...(fleet.summary.runningNow > 0 ? [{ status: 'processing' }] : [])].slice(0, fleet.summary.activeAgents)
    : [];
  const totalActionsToday = fleet?.summary?.actionsToday ?? 0;

  const portcoKpis = selectedPortco === 'all' ? null : safePortcos.find(p => p.id === selectedPortco)?.kpis;
  const denialRate = selectedPortco === 'all'
    ? '4.1%'
    : (portcoKpis?.find(k => k.label.includes('Denial'))?.value ?? '—');
  const daysInAR = selectedPortco === 'all'
    ? '26'
    : (portcoKpis?.find(k => k.label.includes('Wait') || k.label.includes('AR'))?.value ?? '28');

  const fmtTime = (iso: string) => {
    if (!iso) return '—';
    const d = new Date(iso);
    const diff = Date.now() - d.getTime();
    const mins = Math.floor(diff / 60000);
    if (mins < 1) return 'just now';
    if (mins < 60) return `${mins}m ago`;
    const hrs = Math.floor(mins / 60);
    return `${hrs}h ${mins % 60}m ago`;
  };

  const fmtMoney = (n: number) => n >= 1000 ? `$${(n / 1000).toFixed(1)}K` : `$${n.toLocaleString()}`;

  return (
    <div className="space-y-6 pb-8">
      {/* HEADER */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 pb-4 border-b border-slate-200">
        <div className="flex items-center gap-3">
          <img src="/logo.png" alt="Meridian" className="h-14 w-auto object-contain" />
          <div>
            <h1 className="text-xl font-bold text-slate-800 tracking-tight">
              {selectedPortco === 'all' ? 'Portfolio Dashboard' : selectedPortcoName}
            </h1>
            <p className="text-xs text-slate-500">
              {selectedPortco === 'all'
                ? 'Real-time operations across your portfolio'
                : `Operations overview • ${selectedPortcoName}`}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium bg-mantis-50 text-mantis-600 border border-mantis-200">
            <span className={`w-2 h-2 rounded-full bg-mantis-500 ${loading ? 'animate-pulse' : ''}`} />
            {loading ? 'Syncing...' : 'Live'}
          </span>
        </div>
      </div>

      {/* TOP KPI CARDS */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <div
          className="bg-white rounded-xl border border-slate-200 p-4 shadow-sm cursor-pointer hover:border-mantis-300 transition-colors"
          onClick={() => navigate('/workstreams/rcm')}
        >
          <div className="flex items-center justify-between text-slate-500">
            <span className="text-xs font-medium">Recovery at Risk</span>
            <div className="p-1.5 rounded-lg bg-amber-50 text-amber-600"><DollarSign className="w-4 h-4" /></div>
          </div>
          <div className="mt-2 text-2xl font-bold text-slate-800">{loading ? '—' : fmtMoney(denialAmount)}</div>
          <div className="flex items-center gap-1 mt-1 text-xs text-slate-500">
            <AlertTriangle className="w-3.5 h-3.5 text-amber-500" />
            <span>{deniedClaims} denied claims pending</span>
          </div>
        </div>

        <div
          className="bg-white rounded-xl border border-slate-200 p-4 shadow-sm cursor-pointer hover:border-mantis-300 transition-colors"
          onClick={() => navigate('/approvals')}
        >
          <div className="flex items-center justify-between text-slate-500">
            <span className="text-xs font-medium">Pending Approvals</span>
            <div className="p-1.5 rounded-lg bg-redwood-50 text-redwood-500"><AlertTriangle className="w-4 h-4" /></div>
          </div>
          <div className="mt-2 text-2xl font-bold text-slate-800">{loading ? '—' : pendingApprovalsCount}</div>
          <div className="flex items-center gap-1 mt-1 text-xs">
            {pendingApprovalsCount > 0 ? (
              <>
                <span className="text-redwood-600 font-medium">Review needed</span>
                <ChevronRight className="w-3 h-3 text-redwood-400" />
              </>
            ) : (
              <span className="text-mantis-600 font-medium">All clear</span>
            )}
          </div>
        </div>

        <div className="bg-white rounded-xl border border-slate-200 p-4 shadow-sm">
          <div className="flex items-center justify-between text-slate-500">
            <span className="text-xs font-medium">Monthly Savings</span>
            <div className="p-1.5 rounded-lg bg-mantis-50 text-mantis-600"><TrendingUp className="w-4 h-4" /></div>
          </div>
          <div className="mt-2 text-2xl font-bold text-slate-800">{loading ? '—' : monthlySavings}</div>
          <div className="flex items-center gap-1 mt-1 text-xs text-mantis-600 font-medium">
            <ArrowUpRight className="w-3.5 h-3.5" />
            <span>{agentRunsCount} agent runs · {automationHoursSaved.toFixed(0)} hrs saved</span>
          </div>
        </div>

        <div className="bg-white rounded-xl border border-slate-200 p-4 shadow-sm">
          <div className="flex items-center justify-between text-slate-500">
            <span className="text-xs font-medium">Compliance Score</span>
            <div className="p-1.5 rounded-lg bg-mantis-50 text-mantis-600"><Shield className="w-4 h-4" /></div>
          </div>
          <div className="mt-2 text-2xl font-bold text-slate-800">{loading ? '—' : `${complianceScore}%`}</div>
          <div className="flex items-center gap-1 mt-1 text-xs text-mantis-600 font-medium">
            <CheckCircle2 className="w-3.5 h-3.5" />
            <span>HITL enforced · {successRate.toFixed(1)}% appeal success</span>
          </div>
        </div>
      </div>

      {/* MAIN CONTENT GRID */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* LEFT: Live Agent Activity */}
        <div className="lg:col-span-2 space-y-4">
          {/* Agent Activity Feed */}
          <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
            <div className="px-4 py-3 border-b border-slate-100 flex items-center justify-between">
              <h2 className="text-sm font-bold text-slate-700 flex items-center gap-2">
                <Activity className="w-4 h-4 text-mantis-600" />
                Agent Activity
              </h2>
              <span className="text-xs text-slate-400">
                {runningRuns.length > 0
                  ? `${runningRuns.length} running`
                  : `${completedRuns.length} completed`
                }
              </span>
            </div>

            {loading ? (
              <div className="p-8 flex items-center justify-center text-slate-400">
                <Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading agent activity...
              </div>
            ) : recentRuns.length === 0 ? (
              <div className="p-8 text-center">
                <p className="text-sm text-slate-400 mb-1">No agent runs yet</p>
                <p className="text-xs text-slate-400">
                  Trigger agents from the <a href="#/workstreams" className="text-mantis-600 font-medium hover:underline">Agent Fleet</a> page
                </p>
              </div>
            ) : (
              <div className="divide-y divide-slate-50">
                {recentRuns.slice(0, 6).map((run) => (
                  <div key={run.id} className="px-4 py-3 flex items-start gap-3 hover:bg-slate-50 transition-colors">
                    <div className="mt-0.5 shrink-0">
                      {run.status === 'running' ? (
                        <div className="w-8 h-8 rounded-lg bg-blue-50 flex items-center justify-center">
                          <Loader2 className="w-4 h-4 text-blue-500 animate-spin" />
                        </div>
                      ) : (
                        <div className="w-8 h-8 rounded-lg bg-mantis-50 flex items-center justify-center">
                          <CheckCircle2 className="w-4 h-4 text-mantis-600" />
                        </div>
                      )}
                    </div>

                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="text-xs font-semibold text-slate-700">{run.agentName || 'RCM Agent'}</span>
                        <span className={`text-[10px] px-1.5 py-0.5 rounded-full border ${
                          run.status === 'running'
                            ? 'bg-blue-50 text-blue-600 border-blue-200'
                            : 'bg-mantis-50 text-mantis-600 border-mantis-200'
                        }`}>
                          {run.status === 'running' ? 'Processing' : 'Done'}
                        </span>
                      </div>
                      <p className="text-xs text-slate-500 mt-0.5 truncate">
                        {run.summary || `Analyzed ${run.claimsAnalyzed || 0} claims, found ${run.denialsFound || 0} denials`}
                      </p>
                      {run.status === 'completed' && (
                        <div className="flex items-center gap-3 mt-1.5 text-[10px] text-slate-400">
                          <span>{run.claimsAnalyzed || 0} claims analyzed</span>
                          <span>·</span>
                          <span>{run.appealsGenerated || 0} appeals generated</span>
                          {run.avgConfidence > 0 && (
                            <>
                              <span>·</span>
                              <span>{(run.avgConfidence * 100).toFixed(0)}% confidence</span>
                            </>
                          )}
                        </div>
                      )}
                    </div>

                    <div className="text-[10px] text-slate-400 shrink-0 mt-1">
                      {fmtTime(run.startTime)}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Pending Approvals Preview */}
          {approvals.length > 0 && (
            <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
              <div className="px-4 py-3 border-b border-slate-100 flex items-center justify-between">
                <h2 className="text-sm font-bold text-slate-700 flex items-center gap-2">
                  <AlertTriangle className="w-4 h-4 text-redwood-500" />
                  Actions Needing Your Approval
                </h2>
                <button
                  onClick={() => navigate('/approvals')}
                  className="text-xs text-mantis-600 font-medium hover:text-mantis-700 flex items-center gap-1"
                >
                  View all <ChevronRight className="w-3 h-3" />
                </button>
              </div>
              <div className="divide-y divide-slate-50">
                {approvals.slice(0, 4).map((a) => (
                  <div key={a.id} className="px-4 py-3 flex items-center gap-3 hover:bg-slate-50 transition-colors cursor-pointer"
                    onClick={() => navigate('/approvals')}
                  >
                    <div className={`w-2 h-2 rounded-full shrink-0 ${
                      a.priority === 'high' ? 'bg-redwood-500' : a.priority === 'medium' ? 'bg-amber-500' : 'bg-mantis-500'
                    }`} />
                    <div className="flex-1 min-w-0">
                      <p className="text-xs font-medium text-slate-700 truncate">{a.actionDescription}</p>
                      <p className="text-[10px] text-slate-400 mt-0.5">
                        {a.agentName} · {a.portcoName} · {a.impact}
                      </p>
                    </div>
                    <span className={`text-[10px] px-1.5 py-0.5 rounded-full border shrink-0 ${
                      a.priority === 'high'
                        ? 'bg-redwood-50 text-redwood-600 border-redwood-200'
                        : a.priority === 'medium'
                        ? 'bg-amber-50 text-amber-600 border-amber-200'
                        : 'bg-mantis-50 text-mantis-600 border-mantis-200'
                    }`}>
                      {a.priority}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Claims Table */}
          <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
            <div className="px-4 py-3 border-b border-slate-100">
              <div className="flex items-center justify-between w-full">
                <h2 className="text-sm font-bold text-slate-700 flex items-center gap-2">
                  <DollarSign className="w-4 h-4 text-mantis-600" />
                  Denial Recovery Pipeline
                </h2>
                <button
                  onClick={() => downloadPacket(undefined, true)}
                  disabled={packetBusy}
                  className="text-[11px] font-medium flex items-center gap-1 px-2.5 py-1 rounded-lg border border-slate-200 text-slate-600 hover:text-mantis-700 hover:border-mantis-300 hover:bg-mantis-50 disabled:opacity-50 transition-colors"
                >
                  <Download className="w-3 h-3" />
                  {packetBusy ? 'Preparing…' : 'Appeal bundle'}
                </button>
              </div>
            </div>
            {loading ? (
              <div className="p-8 flex items-center justify-center text-slate-400">
                <Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading claims...
              </div>
            ) : !rcmClaims || rcmClaims.claims.length === 0 ? (
              <div className="p-6 text-center text-sm text-slate-400">No denied claims in pipeline</div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-xs">
                  <thead className="bg-slate-50 text-slate-500">
                    <tr>
                      <th className="text-left px-4 py-2 font-medium">Claim ID</th>
                      <th className="text-left px-4 py-2 font-medium">Patient</th>
                      <th className="text-left px-4 py-2 font-medium">Payer</th>
                      <th className="text-left px-4 py-2 font-medium">Denial Reason</th>
                      <th className="text-right px-4 py-2 font-medium">Amount</th>
                      <th className="text-left px-4 py-2 font-medium">Status</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-50">
                    {(() => {
                  const inflight = (x: string) => x === 'appeal_submitted' || x === 'appeal_approved';
                  return [...rcmClaims.claims]
                    .sort((a, b) => (inflight(b.status) ? 1 : 0) - (inflight(a.status) ? 1 : 0) || b.claimAmount - a.claimAmount)
                    .slice(0, 10);
                })().map((c) => (
                      <tr key={c.id} className="hover:bg-slate-50 transition-colors">
                        <td className="px-4 py-2 font-mono text-slate-600">{c.claimId}</td>
                        <td className="px-4 py-2 text-slate-700">{c.patientName}</td>
                        <td className="px-4 py-2 text-slate-600">{c.payerName}</td>
                        <td className="px-4 py-2 text-slate-500 max-w-[180px] truncate">{c.denialReason}</td>
                        <td className="px-4 py-2 text-right font-medium text-slate-700">${c.claimAmount.toLocaleString()}</td>
                        <td className="px-4 py-2">
                          <div className="flex items-center gap-1.5">
                            <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-amber-50 text-amber-600 border border-amber-200">
                              {c.status}
                            </span>
                            {(c.status === 'appeal_submitted' || c.status === 'appeal_approved') && (
                              <button
                                onClick={() => downloadPacket(c.id)}
                                className="text-[10px] font-medium flex items-center gap-0.5 px-1.5 py-0.5 rounded-md border border-mantis-200 text-mantis-700 hover:bg-mantis-50 transition-colors"
                                title="Printable appeal packet"
                              >
                                <Download className="w-2.5 h-2.5" /> Packet
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>

        {/* RIGHT: Sidebar widgets */}
        <div className="space-y-4">
          {/* Active Agents */}
          <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
            <div className="px-4 py-3 border-b border-slate-100">
              <h2 className="text-sm font-bold text-slate-700 flex items-center gap-2">
                <Cpu className="w-4 h-4 text-mantis-600" />
                Active Agents
              </h2>
            </div>
            <div className="p-3 space-y-2">
              {filteredAgents.slice(0, 6).map((agent) => (
                <div key={agent.id} className="flex items-center gap-2.5 p-2 rounded-lg hover:bg-slate-50 transition-colors">
                  <div className={`w-7 h-7 rounded-lg flex items-center justify-center shrink-0 ${
                    agent.status === 'active' ? 'bg-mantis-50' :
                    agent.status === 'running' ? 'bg-blue-50' : 'bg-amber-50'
                  }`}>
                    {agent.status === 'running' ? (
                      <Loader2 className="w-3.5 h-3.5 text-blue-500 animate-spin" />
                    ) : (
                      <Cpu className={`w-3.5 h-3.5 ${
                        agent.status === 'active' ? 'text-mantis-600' : 'text-amber-500'
                      }`} />
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-xs font-medium text-slate-700 truncate">{agent.name}</p>
                    <p className="text-[10px] text-slate-400">{agent.actionsToday} actions today · {agent.successRate}%</p>
                  </div>
                  <div className={`w-2 h-2 rounded-full shrink-0 ${
                    agent.status === 'active' ? 'bg-mantis-500' :
                    agent.status === 'running' ? 'bg-blue-500 animate-pulse' : 'bg-amber-400'
                  }`} />
                </div>
              ))}
            </div>
          </div>

          {/* Per-PortCo Comparison (only when viewing all) */}
          {selectedPortco === 'all' && (
            <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
              <div className="px-4 py-3 border-b border-slate-100">
                <h2 className="text-sm font-bold text-slate-700 flex items-center gap-2">
                  <TrendingUp className="w-4 h-4 text-mantis-600" />
                  Portfolio Comparison
                </h2>
              </div>
              <div className="p-3 space-y-3">
                {safePortcos.map((p) => (
                  <div key={p.id} className="space-y-1.5">
                    <div className="flex items-center justify-between">
                      <span className="text-xs font-medium text-slate-700">{p.name}</span>
                      <span className="text-xs font-bold text-slate-800">{p.monthlyRevenue}</span>
                    </div>
                    <div className="flex items-center gap-3 text-[10px] text-slate-400">
                      <span>{p.patients.toLocaleString()} patients</span>
                      <span>·</span>
                      <span>{p.staff} staff</span>
                      <span>·</span>
                      <span className={p.complianceScore >= 90 ? 'text-mantis-600 font-medium' : 'text-amber-600 font-medium'}>
                        {p.complianceScore}% compliance
                      </span>
                    </div>
                    <div className="h-1.5 bg-slate-100 rounded-full overflow-hidden">
                      <div
                        className={`h-full rounded-full ${p.complianceScore >= 90 ? 'bg-mantis-500' : 'bg-amber-400'}`}
                        style={{ width: `${p.complianceScore}%` }}
                      />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Quick Stats */}
          <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-4">
            <h2 className="text-sm font-bold text-slate-700 mb-3 flex items-center gap-2">
              <FileText className="w-4 h-4 text-mantis-600" />
              At a Glance
            </h2>
            <div className="space-y-2.5">
              <div className="flex items-center justify-between text-xs">
                <span className="text-slate-500">Businesses</span>
                <span className="font-medium text-slate-700">{safePortcos.length} active</span>
              </div>
              <div className="flex items-center justify-between text-xs">
                <span className="text-slate-500">Total Patients</span>
                <span className="font-medium text-slate-700">{totalPatients}</span>
              </div>
              <div className="flex items-center justify-between text-xs">
                <span className="text-slate-500">Total Staff</span>
                <span className="font-medium text-slate-700">{totalStaff}</span>
              </div>
              <div className="flex items-center justify-between text-xs">
                <span className="text-slate-500">Agent Runs (30d)</span>
                <span className="font-medium text-slate-700">{agentRunsCount}</span>
              </div>
              <div className="flex items-center justify-between text-xs">
                <span className="text-slate-500">Denial Rate</span>
                <span className="font-medium text-slate-700">{denialRate}</span>
              </div>
              <div className="flex items-center justify-between text-xs">
                <span className="text-slate-500">Days in AR</span>
                <span className="font-medium text-slate-700">{daysInAR}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
